import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { MotionConfig } from 'framer-motion';
import { Eye, MessageSquare, WifiOff } from 'lucide-react';
import Badge from '../../../ui/Badge';
import Tooltip from '../../../ui/Tooltip';
import SchemaModal from '../../../ui/SchemaModal';
import Skeleton from '../../../ui/Skeleton';
import { ToastPlacement, useToast } from '../../../ui/Toast';
import { useIsMobileViewport } from '../../../ui/breakpoints';
import { useLongPress } from '../../../hooks/useLongPress';
import { usePlaytest } from '../../../context/PlaytestContext';
import CommanderTaxMenu from './CommanderTaxMenu';
import { usePlaytestSocket } from '../hooks/usePlaytestSocket';
import { useCamera } from '../hooks/useCamera';
import { DROP_IDS, LONG_PRESS_MS, usePlaytestDnd } from '../hooks/usePlaytestDnd';
import { useDeferredTap } from '../hooks/useDeferredTap';
import { useHoverPreview } from '../hooks/useHoverPreview';
import { useOrientationLock } from '../hooks/useOrientationLock';
import { getCardSizePx } from '../utils/boardGeometry';
import { mergeHandlers } from '../utils/eventHandlers';
import {
  battlefieldCardActions,
  boardActions,
  deckActions,
  handCardActions,
  opponentCardActions,
  sessionActions,
  spectatorCardActions,
  zoneCardActions,
} from '../utils/cardActions';
import Battlefield from './Battlefield';
import BattlefieldContextMenu from './BattlefieldContextMenu';
import HandFan from './HandFan';
import BoardDock from './BoardDock';
import ZoneTile, { ZONES } from './ZoneTile';
import CardListViewer from './CardListViewer';
import ScryMillDialog from './ScryMillDialog';
import ParticipantRow from './ParticipantRow';
import ActionRail from './ActionRail';
import ActionSheet from './ActionSheet';
import CardActionSheet from './CardActionSheet';
import { ActionContextMenu, CardContextMenu } from './PlaytestCardContextMenu';
import DragOverlayCard from './DragOverlayCard';
import FloatingCardPreview from './FloatingCardPreview';
import MulliganOverlay from './MulliganOverlay';
import PlaytestDialogs from './PlaytestDialogs';
import TokenPicker from './TokenPicker';
import ProtocolSheet from './ProtocolSheet';
import PortraitHint from './PortraitHint';
import './PlaytestBoardPage.css';

const sameUser = (a, b) => a != null && b != null && String(a) === String(b);

/** Fehler, nach denen die Seite ohne Rückfrage zum Einstieg zurückkehrt (der Toast erklärt). */
const LEAVE_TO_ENTRY_CODES = new Set(['NOT_SESSION_MEMBER', 'SESSION_NOT_FOUND']);

/**
 * Android meldet einen Langdruck zusätzlich als `contextmenu`; derselbe Druck öffnet das
 * Karten-Sheet deshalb nur einmal, und ein Tipp direkt danach tappt die Karte nicht.
 */
const SHEET_DEDUPE_MS = 400;

/**
 * Abstand über dem Dock und Größe des Protokoll-Knopfs; mit der gemessenen Dock-Höhe folgen daraus
 * die Höhe, die der Aktionsleiste bleibt, und die Unterkante der Toasts. Die CSS liest dieselben
 * Werte als Variablen.
 */
const DOCK_SPACING = { gapPx: 8, protocolPx: 36 };

const SHORTCUTS = [
  ['R', 'Andere Seite in der Großansicht ansehen (nur für dich)'],
  ['T', 'Tappen / Enttappen'],
  ['F', 'In den Friedhof'],
  ['E', 'Ins Exil'],
  ['H', 'Auf die Hand'],
  ['D', 'Unter die Bibliothek'],
  ['P', 'Hinweis geben'],
  ['Doppelklick', 'Hinweis geben'],
  ['Rechtsklick', 'Aktionen der Karte oder Fläche'],
  ['Mausrad', 'Zoomen'],
  ['Ziehen auf freier Fläche', 'Brett schwenken'],
];

const SHORTCUT_MOVES = {
  f: { toZone: 'graveyard' },
  e: { toZone: 'exile' },
  h: { toZone: 'hand' },
  d: { toZone: 'deck', insertAtTop: false },
};

const DROP_LABELS = {
  [DROP_IDS.battlefield]: 'dem Brett',
  [DROP_IDS.graveyard]: 'dem Friedhof',
  [DROP_IDS.exile]: 'dem Exil',
  [DROP_IDS.deckTop]: 'der Bibliothek (oben)',
  [DROP_IDS.deckBottom]: 'der Bibliothek (unten)',
  [DROP_IDS.hand]: 'der Hand',
  [DROP_IDS.commandZone]: 'der Commander-Zone',
};

const COMMANDER_TAX_ID = 'commander-tax';

const draggedName = (active) => active?.data?.current?.card?.scryfallData?.name || 'Karte';
const dropLabel = (over) => DROP_LABELS[over?.id] || 'einem Ziel';

/** Ansagen für Screenreader beim Ziehen; dnd-kit liefert sonst englische Texte. */
const DND_ACCESSIBILITY = {
  screenReaderInstructions: {
    draggable: 'Leertaste nimmt die Karte auf, die Pfeiltasten bewegen sie, Leertaste legt sie ab, Escape bricht ab.',
  },
  announcements: {
    onDragStart: ({ active }) => `${draggedName(active)} aufgenommen.`,
    onDragOver: ({ active, over }) => (over
      ? `${draggedName(active)} über ${dropLabel(over)}.`
      : `${draggedName(active)} über keinem Ziel.`),
    onDragEnd: ({ active, over }) => (over
      ? `${draggedName(active)} auf ${dropLabel(over)} abgelegt.`
      : `${draggedName(active)} ohne Ziel losgelassen.`),
    onDragCancel: ({ active }) => `Ziehen von ${draggedName(active)} abgebrochen.`,
  },
};

function topOf(cards) {
  const list = (cards || []).filter(Boolean);
  return list.length > 0 ? list[list.length - 1] : null;
}

function isTypingTarget(target) {
  return !!target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]');
}

/**
 * Höhe eines Elements, laufend gemessen (Platz für Aktionsleiste, Protokoll-Knopf und Toasts).
 * Gemessen wird vor dem ersten Zeichnen, sonst stünden Knopf und Toasts ein Bild lang im Dock.
 */
function useElementHeight() {
  const [node, setNode] = useState(null);
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    if (!node) return undefined;
    const measure = () => setHeight(node.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, height];
}

/**
 * Das Playtest-Brett (Spec 2026-10-21 §5.5): setzt Hooks und Bausteine zusammen, ohne eigene
 * Brettlogik.
 *
 *   <PlaytestBoardPage sessionId={sessionId} />   // unter PlaytestBoardRoute, Zustand initialisiert
 *
 * - Rechner: die Brettfläche reicht von Rand zu Rand; oben schweben die Teilnehmer-Köpfe als
 *   einzelne Panels (`ParticipantRow floating`), darunter links die Aktionsleiste. Unten schwebt
 *   das Dock (`BoardDock`) über dem Brett: links Friedhof (außen) und Exil, in der Mitte der
 *   Handfächer, rechts Commander-Zone (Krone mit Steuer-Menü) und Bibliothek; in Ruhe ragt
 *   es halb ins Bild, mit dem Zeiger darüber ganz — auch während eines Zugs hebt es erst der
 *   Zeiger im Dock-Streifen, nicht der Zugstart. Über dem sichtbaren
 *   Teil des Docks am rechten Rand der Protokoll-Knopf. Brettkarten dürfen unter dem Dock
 *   liegen; per Schwenken sind sie erreichbar (Arena-Vorbild).
 * - Toasts stehen am rechten Rand über dem gehobenen Dock (`ToastPlacement`), damit die gehobene
 *   Bibliothek sie nicht verdeckt. Leiste und Protokoll-Knopf rechnen mit dem Dock in Ruhe;
 *   gehoben liegt es vorübergehend darüber.
 * - Handy quer: Köpfe als schwebende Chips, rechts daneben Zug und „Zug beenden", schmale Leiste
 *   rechts darunter, unten dasselbe Dock. Ein Tipp hebt es, ein Tipp außerhalb senkt es; beim
 *   Ziehen bleibt es gehoben und nur die Hand-Reihe rutscht nach unten, nach dem Ablegen senkt es sich.
 * - Handy hochkant: `PortraitHint` statt des Bretts, offene Sheets schließen, ein laufender Zug
 *   bricht ab.
 * - Zieh-Quellen: Handkarten, eigene Brettkarten, die Oberkarten der eigenen Zonen-Kacheln und
 *   die Karten eines eigenen Zonen-Viewers; Ziel Brett oder Zone (`usePlaytestDnd`). Bei offenem
 *   Viewer ziehen nur dessen Karten. Am Handy ist die offene Oberkarte der Bibliothek keine
 *   Zieh-Quelle: der Langdruck dort öffnet das Bibliotheks-Sheet.
 * - Ausgeschiedene sehen zu (`canAct` false, Hinweis „Du siehst zu"): kein Ziehen, Tappen,
 *   Zonen-Aktionen, Leben ändern, „Enttappen & ziehen", Würfeln; Karten-Menüs bieten nur den
 *   Hinweis-Ping. Ping, Protokoll, Zonen ansehen, „Verlassen" und die Host-Aufgaben bleiben.
 * - Verbindung: während Socket.io neu verbindet, steht oben ein Offline-Marker; geben die
 *   Versuche auf, fragt ein Modal „Neu verbinden" oder zurück zum Einstieg.
 * - Tippen/Doppelklick auf eine Brettkarte (R1): der Klick dreht die Karte über eine lokale
 *   Überlagerung sofort (`tapOverrides`, angezeigt als `tapped XOR Überlagerung`), gesendet wird
 *   erst nach der Doppelklick-Frist von `useDeferredTap`.
 * - Karten-Aktionen: Rechner Punkt-Menü (`CardContextMenu`), Handy `CardActionSheet` über den
 *   Langdruck aus `usePlaytestDnd` (eigene Karten), sonst über einen Langdruck auf der Fläche
 *   (fremde Karten, freie Fläche) oder das `contextmenu` von Android — je Druck nur einmal.
 * - Tastenkürzel (Rechner) t/f/e/h/d/p auf der gehoverten, sonst der zuletzt getappten Karte; r dreht
 *   nur die stehende Großansicht (`useHoverPreview` `toggleSide`), auch für Zuschauer.
 * - Kartenbilder auf Brett, Hand, Kacheln und Mulligan zeigen die Server-Seite; der Umdreh-Knopf
 *   steht nur an der Karte der stehenden Großansicht (`previewFlipFor`) und dreht nur diese.
 * - Toasts: Würfelergebnisse und Verbindungsfehler; Aktionsfehler, „Du bist dran" und
 *   Kontroll-Meldungen toastet der Socket-Hook.
 *   `state.boardClosed` mit NOT_SESSION_MEMBER/SESSION_NOT_FOUND → Toast und zurück zum
 *   Einstieg, jeder andere Grund → Modal aus `PlaytestDialogs`.
 */
export default function PlaytestBoardPage({ sessionId }) {
  const { state, dispatch } = usePlaytest();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useIsMobileViewport();
  const { isPortrait, requestLandscape } = useOrientationLock({ enabled: isMobile });
  const showPortraitHint = isMobile && isPortrait;

  const me = state.players.find((p) => sameUser(p.userId, state.currentUserId)) || null;
  const playerCount = state.players.length;
  const isHost = sameUser(state.hostId, state.currentUserId);
  const isMyTurn = !!me && sameUser(state.currentTurnUserId, me.userId) && !state.isInMulliganPhase;
  // Ausgeschiedene sehen bis zum Zurücksetzen nur zu; der Server lehnt ihre Aktionen ohnehin ab.
  const canAct = me?.playerStatus !== 'eliminated';

  // ---------------- Overlays: gemessene Höhen ----------------
  const [dockRef, dockHeightPx] = useElementHeight();
  const [headsRef, headsHeightPx] = useElementHeight();
  const [headsExpanded, setHeadsExpanded] = useState(false);
  // Das Dock ragt in Ruhe mit der halben Höhe ins Bild; darüber stehen Protokoll-Knopf und Toasts.
  const dockRestPx = Math.round((dockHeightPx ?? 0) / 2);
  // Unter den schwebenden Köpfen beginnen Aktionsleiste und der freie Blick aufs Brett. Ein am
  // Platz aufgeklappter Chip macht die Zeile nur vorübergehend höher: Leiste und Kamera bleiben
  // bei der eingeklappten Höhe, sonst sprängen sie bei jedem Aufklappen mit.
  const collapsedHeadsRef = useRef(null);
  // Beim Einklappen hält `headsHeightPx` bis zur nächsten Messung noch die aufgeklappte Höhe:
  // sie wird nie als eingeklappte Höhe übernommen.
  const expandedHeadsRef = useRef(null);
  if (headsExpanded) {
    if (headsHeightPx !== collapsedHeadsRef.current) expandedHeadsRef.current = headsHeightPx;
  } else if (headsHeightPx !== expandedHeadsRef.current || collapsedHeadsRef.current == null) {
    collapsedHeadsRef.current = headsHeightPx;
  }
  const headsBasePx = collapsedHeadsRef.current;
  const headsReservePx = headsBasePx == null ? 0 : Math.ceil(headsBasePx) + DOCK_SPACING.gapPx;

  // ---------------- Kamera und Socket ----------------
  const viewportRef = useRef(null);
  const camera = useCamera({
    viewportRef,
    playerCount,
    viewerPosition: me?.position ?? 0,
    insets: { top: headsReservePx, bottom: dockRestPx },
  });
  const playersRef = useRef(state.players);
  playersRef.current = state.players;

  const focusCard = useCallback((instanceId) => {
    const card = playersRef.current.flatMap((p) => p.battlefield || []).find((c) => c?.instanceId === instanceId);
    if (card?.position) camera.centerOn(card.position);
  }, [camera]);

  const socket = usePlaytestSocket(sessionId, { onFocusCard: focusCard });

  const taxValue = me?.counters?.find((c) => c.id === COMMANDER_TAX_ID)?.value ?? 0;
  const changeCommanderTax = useCallback((change) => {
    const base = { counterId: COMMANDER_TAX_ID, name: 'Commander-Steuer', icon: 'crown' };
    socket.updatePlayerCounter(change?.reset ? { ...base, value: 0 } : { ...base, delta: change });
  }, [socket]);
  const cardSizePx = camera.camera ? getCardSizePx(camera.camera.zoom) : 0;

  // ---------------- Lokaler Anzeigezustand ----------------
  const [dockRaised, setDockRaised] = useState(false);
  const [viewer, setViewer] = useState(null); // { zone, userId, ids? }
  const [scryMill, setScryMill] = useState(null); // 'scry' | 'mill'
  const [tokenPosition, setTokenPosition] = useState(undefined); // undefined = zu, null = Server-Vorgabe
  const [protocolOpen, setProtocolOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [menu, setMenu] = useState(null); // { point, target }
  const [sheetTarget, setSheetTarget] = useState(null);
  const [boardSheet, setBoardSheet] = useState(null); // { position }
  const [deckSheetOpen, setDeckSheetOpen] = useState(false);
  const [tapOverrides, setTapOverrides] = useState(() => new Set());
  const lastSheetRef = useRef(null);
  const hoveredRef = useRef(null);
  const selectedRef = useRef(null);
  const landscapeRequestedRef = useRef(false);

  // ---------------- Karten finden ----------------
  const findCard = useCallback((target) => {
    if (!target) return null;
    if (target.zone === 'battlefield') {
      for (const player of state.players) {
        const card = (player.battlefield || []).find((c) => c?.instanceId === target.instanceId);
        if (card) return { card, holderId: player.userId };
      }
      return null;
    }
    const owner = state.players.find((p) => sameUser(p.userId, target.ownerId));
    const card = (owner?.[target.zone] || []).find((c) => c?.instanceId === target.instanceId);
    return card ? { card, holderId: owner.userId } : null;
  }, [state.players]);

  const battlefieldTarget = useCallback((instanceId) => {
    const holder = state.players.find((p) => (p.battlefield || []).some((c) => c?.instanceId === instanceId));
    return { instanceId, zone: 'battlefield', ownerId: holder?.userId };
  }, [state.players]);

  // ---------------- Aktionen ----------------
  const boardPosition = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const r = el.getBoundingClientRect();
    return camera.toWorld({ x: r.left + r.width / 2, y: r.top + r.height / 2 }) || undefined;
  }, [camera]);

  const openViewer = useCallback((zone, userId = state.currentUserId) => {
    setViewer({ zone, userId });
  }, [state.currentUserId]);

  const ctx = useMemo(() => ({
    ...socket,
    openViewer: (zone) => openViewer(zone),
    openScryMill: (mode) => setScryMill(mode),
    boardPosition,
    deckOpenPlay: !!me?.deckOpenPlay,
  }), [socket, openViewer, boardPosition, me?.deckOpenPlay]);

  const actionsFor = useCallback((target) => {
    const found = findCard(target);
    if (!found) return { card: null, actions: [] };
    const { card, holderId } = found;
    const own = sameUser(holderId, state.currentUserId);
    if (!canAct) return { card, actions: target.zone === 'battlefield' ? spectatorCardActions(card, ctx) : [] };
    if (target.zone === 'battlefield') {
      return { card, actions: own ? battlefieldCardActions(card, ctx) : opponentCardActions(card, ctx) };
    }
    if (!own) return { card, actions: [] };
    if (target.zone === 'hand') return { card, actions: handCardActions(card, ctx) };
    return { card, actions: zoneCardActions(card, target.zone, ctx) };
  }, [findCard, ctx, state.currentUserId, canAct]);

  const openCardActions = useCallback((target, point) => {
    if (!isMobile) {
      setMenu({ point, target });
      return;
    }
    const last = lastSheetRef.current;
    const now = Date.now();
    if (last && last.id === target.instanceId && now - last.at < SHEET_DEDUPE_MS) return;
    lastSheetRef.current = { id: target.instanceId, at: now };
    setSheetTarget(target);
  }, [isMobile]);

  // R1: Klick dreht sofort lokal, gesendet wird nach der Doppelklick-Frist.
  const { tapCard, pingCard } = socket;
  const onTapSend = useCallback((instanceId, opts) => {
    setTapOverrides((prev) => {
      const next = new Set(prev);
      if (opts.optimisticOnly) next.add(instanceId);
      else next.delete(instanceId);
      return next;
    });
    if (opts.send) tapCard(instanceId);
  }, [tapCard]);
  const deferredTap = useDeferredTap({ onTapSend, onPing: pingCard });

  const displayPlayers = useMemo(() => {
    if (tapOverrides.size === 0) return state.players;
    return state.players.map((p) => ({
      ...p,
      battlefield: (p.battlefield || []).map((c) => (
        c && tapOverrides.has(c.instanceId) ? { ...c, tapped: !c.tapped } : c
      )),
    }));
  }, [state.players, tapOverrides]);

  const onCardTap = useCallback((instanceId) => {
    const last = lastSheetRef.current;
    if (last && last.id === instanceId && Date.now() - last.at < SHEET_DEDUPE_MS) return;
    selectedRef.current = instanceId;
    const card = displayPlayers.flatMap((p) => p.battlefield || []).find((c) => c?.instanceId === instanceId);
    deferredTap.onClick(instanceId, !!card?.tapped);
  }, [displayPlayers, deferredTap]);

  // ---------------- Vorschau ----------------
  const hover = useHoverPreview();
  const { bind: hoverBind, clear: clearHover, preview, toggleSide } = hover;
  // Der Umdreh-Knopf steht nur an der Karte, deren Großansicht gerade steht, und dreht nur diese.
  const previewFlipFor = useCallback(
    (card) => (card && preview?.card?.instanceId === card.instanceId ? toggleSide : undefined),
    [preview, toggleSide],
  );
  const cardHover = useCallback((card) => mergeHandlers(hoverBind(card), {
    onPointerEnter: () => { hoveredRef.current = card.instanceId; },
    onPointerLeave: () => { if (hoveredRef.current === card.instanceId) hoveredRef.current = null; },
  }), [hoverBind]);

  // ---------------- Ziehen ----------------
  const dnd = usePlaytestDnd({
    actions: socket,
    toWorld: camera.toWorld,
    cardSizePx,
    playerCount,
    onLongPress: (instanceId, fromZone) => {
      const target = fromZone === 'battlefield'
        ? battlefieldTarget(instanceId)
        : { instanceId, zone: fromZone, ownerId: viewer?.userId ?? state.currentUserId };
      openCardActions(target);
    },
  });

  const onDragStart = (event) => {
    clearHover();
    dnd.onDragStart(event);
  };

  // Am Handy senkt sich das Dock nach dem Ablegen; am Rechner bestimmt danach wieder der Zeiger.
  const onDragEnd = (event) => {
    if (isMobile) setDockRaised(false);
    dnd.onDragEnd(event);
  };
  const onDragCancel = (event) => {
    if (isMobile) setDockRaised(false);
    dnd.onDragCancel(event);
  };

  // Der Hochformat-Hinweis hängt das Brett samt DndContext aus; ein laufender Zug endet dabei
  // ohne onDragCancel, und das Overlay der Karte stünde nach dem Zurückdrehen noch da.
  const { onDragCancel: cancelDrag } = dnd;
  const closeAllSurfaces = useCallback(() => {
    setViewer(null);
    setScryMill(null);
    setTokenPosition(undefined);
    setProtocolOpen(false);
    setShortcutsOpen(false);
    setMenu(null);
    setSheetTarget(null);
    setBoardSheet(null);
    setDeckSheetOpen(false);
    setDockRaised(false);
    cancelDrag();
  }, [cancelDrag]);

  useEffect(() => {
    if (showPortraitHint) closeAllSurfaces();
  }, [showPortraitHint, closeAllSurfaces]);

  // ---------------- Langdruck auf der Fläche (Handy) ----------------
  const boardPressRef = useRef(null);
  const boardLongPress = useLongPress(useCallback(() => {
    const press = boardPressRef.current;
    if (!press) return;
    if (press.instanceId) {
      openCardActions(battlefieldTarget(press.instanceId));
      return;
    }
    if (canAct) setBoardSheet({ position: camera.toWorld(press.point) || undefined });
  }, [openCardActions, battlefieldTarget, camera, canAct]), { delayMs: LONG_PRESS_MS });

  const boardPressHandlers = {
    ...boardLongPress,
    onPointerDown: (e) => {
      // Nur die Brettfläche; eigene Karten erkennt der Langdruck des Ziehens (usePlaytestDnd).
      if (!e.target.closest?.('[data-testid="battlefield"]') || e.target.closest('.battlefield-card--own')) {
        boardPressRef.current = null;
        return;
      }
      const cardEl = e.target.closest?.('[data-testid="battlefield-card"]');
      boardPressRef.current = { point: { x: e.clientX, y: e.clientY }, instanceId: cardEl?.dataset.instanceId || null };
      boardLongPress.onPointerDown(e);
    },
  };

  const deckLongPress = useLongPress(useCallback(() => setDeckSheetOpen(true), []), { delayMs: LONG_PRESS_MS });

  // ---------------- Rückmeldungen ----------------
  const lastRollRef = useRef(state.lastDiceRoll?.rollId ?? 0);
  useEffect(() => {
    const roll = state.lastDiceRoll;
    if (!roll || roll.rollId === lastRollRef.current) return;
    lastRollRef.current = roll.rollId;
    const who = sameUser(roll.userId, state.currentUserId) ? 'Du' : (roll.displayName || 'Teilnehmer');
    const own = who === 'Du';
    const message = roll.sides === 'coin'
      ? `${who} ${own ? 'wirfst' : 'wirft'} eine Münze: ${roll.result}`
      : `${who} ${own ? 'würfelst' : 'würfelt'} W${roll.sides}: ${roll.result}`;
    toast.push({ tone: 'info', message });
  }, [state.lastDiceRoll, state.currentUserId, toast]);

  useEffect(() => {
    if (socket.error) toast.push({ tone: 'error', message: socket.error });
  }, [socket.error, toast]);

  const leaveToEntry = useCallback(() => navigate('/playtest', { replace: true }), [navigate]);
  useEffect(() => {
    const closed = state.boardClosed;
    if (!closed || !LEAVE_TO_ENTRY_CODES.has(closed.code)) return;
    toast.push({ tone: 'error', message: closed.message });
    leaveToEntry();
  }, [state.boardClosed, toast, leaveToEntry]);

  // ---------------- Tastenkürzel (Rechner) ----------------
  const shortcutRef = useRef(null);
  shortcutRef.current = { players: state.players, me: state.currentUserId, socket, canAct, toggleSide };
  useEffect(() => {
    if (isMobile) return undefined;
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      const key = e.key?.toLowerCase();
      // R dreht nur die eigene Großansicht: auch ohne `canAct` und ohne Karte unter dem Zeiger.
      if (key === 'r') {
        shortcutRef.current.toggleSide();
        return;
      }
      if (!['t', 'p', ...Object.keys(SHORTCUT_MOVES)].includes(key)) return;
      const { players, me: viewerId, socket: act, canAct: acts } = shortcutRef.current;
      const instanceId = hoveredRef.current || selectedRef.current;
      if (!instanceId) return;
      const holder = players.find((p) => (p.battlefield || []).some((c) => c?.instanceId === instanceId));
      if (!holder) return;
      if (key === 'p') {
        act.pingCard(instanceId);
        return;
      }
      if (!acts || !sameUser(holder.userId, viewerId)) return;
      if (key === 't') act.tapCard(instanceId);
      else act.moveCardZone({ instanceId, fromZone: 'battlefield', ...SHORTCUT_MOVES[key] });
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile]);

  // ---------------- Abgeleitete Listen ----------------
  const more = sessionActions({
    isHost,
    playerStatus: me?.playerStatus,
    onOverview: camera.goOverview,
    onOpenProtocol: () => setProtocolOpen(true),
    onOpenShortcuts: isMobile ? undefined : () => setShortcutsOpen(true),
    onEliminate: socket.eliminate,
    onLeave: () => {
      socket.leave();
      navigate('/playtest');
    },
    onReset: socket.resetPlaytest,
    onEndSession: () => {
      socket.endSession();
      navigate('/playtest');
    },
  });

  const untapAndDraw = () => {
    socket.untapAll();
    socket.drawCard(1);
  };

  const menuData = menu ? actionsFor(menu.target) : null;
  const sheetData = sheetTarget ? actionsFor(sheetTarget) : null;
  // Verlässt die Karte ihren Ort (Menü-Eintrag, Server-Ereignis), schließt die Fläche nur über die
  // Daten; ohne Aufräumen öffnete sie sich wieder, sobald die Karte zurückkehrt. Radix meldet das
  // Schließen nach einer Auswahl nicht mehr, weil der Eintrag die Karte schon synchron bewegt hat.
  const menuCardGone = !!menu && !menuData?.card;
  const sheetCardGone = !!sheetTarget && !sheetData?.card;
  useEffect(() => {
    if (menuCardGone) setMenu(null);
  }, [menuCardGone]);
  useEffect(() => {
    if (sheetCardGone) setSheetTarget(null);
  }, [sheetCardGone]);

  const viewerOwner = viewer ? state.players.find((p) => sameUser(p.userId, viewer.userId)) : null;
  const viewerIsOwn = !!viewer && sameUser(viewer.userId, state.currentUserId);
  const viewerCards = useMemo(() => {
    if (!viewer || !viewerOwner) return [];
    const cards = (viewerOwner[viewer.zone] || []).filter(Boolean);
    if (viewer.zone !== 'deck') return [...cards].reverse();
    // Fremde offene Bibliothek: immer die aktuelle Oberkarte, auch wenn sie seit dem Öffnen wechselt.
    if (!viewerIsOwn) return viewerOwner.deckOpenPlay ? [topOf(viewerOwner.deck)].filter(Boolean) : [];
    const topFirst = [...cards].reverse();
    return viewer.ids ? topFirst.filter((c) => viewer.ids.includes(c.instanceId)) : topFirst;
  }, [viewer, viewerOwner, viewerIsOwn]);
  const viewerLabel = viewer && (viewer.zone === 'deck' && !viewerIsOwn ? 'Bibliothek (oben)'
    : viewer.ids ? `Scry ${viewer.ids.length}` : ZONES[viewer.zone].label);
  const viewerTitle = viewer ? `${viewerLabel}${viewerIsOwn ? '' : ` · ${viewerOwner?.displayName || 'Teilnehmer'}`}` : '';
  const viewerMenuOpen = !!viewer && !!menu && menu.target.zone === viewer.zone && viewer.zone !== 'battlefield';
  const viewerSheetOpen = !!viewer && !!sheetTarget && sheetTarget.zone === viewer.zone;

  const confirmScryMill = (n) => {
    const deck = (me?.deck || []).filter(Boolean);
    const topIds = deck.slice(-n).reverse().map((c) => c.instanceId);
    if (scryMill === 'scry') setViewer({ zone: 'deck', userId: state.currentUserId, ids: topIds });
    else socket.moveCardZone({ instanceId: topIds, fromZone: 'deck', toZone: 'graveyard' });
  };

  const pickToken = (tokenCardId, tokenData) => {
    if (tokenData) socket.addTokenToAvailable(tokenData);
    socket.createToken(tokenCardId, tokenPosition ?? undefined);
  };

  const closedState = state.boardClosed && LEAVE_TO_ENTRY_CODES.has(state.boardClosed.code)
    ? { ...state, boardClosed: null }
    : state;
  const dialogs = (
    <PlaytestDialogs
      state={closedState}
      dispatch={dispatch}
      actions={{ respondViewHand: socket.respondViewHand, respondCardControl: socket.respondCardControl }}
      onLeaveToEntry={leaveToEntry}
    />
  );

  if (!socket.syncedOnce || !me) {
    return (
      <div className="playtest-board playtest-board--loading" data-testid="playtest-board-loading">
        <Skeleton width="min(480px, 80vw)" height="48px" radius="lg" label="Brett wird geladen" />
        <Skeleton width="min(720px, 90vw)" height="40vh" radius="lg" />
        <Skeleton width="min(560px, 85vw)" height="96px" radius="lg" />
        {dialogs}
      </div>
    );
  }

  if (showPortraitHint) {
    return (
      <div className="playtest-board playtest-board--portrait" data-testid="playtest-board">
        <PortraitHint
          life={me.life}
          turnNumber={state.turnNumber}
          canEndTurn={isMyTurn}
          onEndTurn={socket.endTurn}
          onRequestLandscape={requestLandscape}
        />
        {dialogs}
      </div>
    );
  }

  // Toasts stehen über dem gehobenen Dock, sonst verdeckte die gehobene Bibliothek rechts sie; und
  // in jedem Fall über dem Protokoll-Knopf.
  const toastBottomPx = Math.max(
    Math.round(dockHeightPx ?? 0),
    dockRestPx + DOCK_SPACING.gapPx + DOCK_SPACING.protocolPx,
  ) + DOCK_SPACING.gapPx;
  // Platz, den die Aktionsleiste unten frei lässt: links (Rechner) nur das Dock, rechts (Handy)
  // zusätzlich der Protokoll-Knopf.
  const railDockReservePx = dockRestPx + DOCK_SPACING.gapPx + (isMobile ? DOCK_SPACING.protocolPx + DOCK_SPACING.gapPx : 0);
  // Am Handy hebt ein Zug das Dock, damit jede Kachel ganz als Ablageziel bereitsteht; nur die
  // Hand-Reihe rutscht dabei nach unten und gibt das Brett frei. Am Rechner bleibt es in Ruhe,
  // bis der Zeiger mit der Karte in den Dock-Streifen kommt (Lage-Hover in `BoardDock`, der auch
  // während eines Zugs läuft) — ein Heben schon beim Zugstart verdeckte den unteren Brettrand.
  // Ein Fingerzug hebt das Dock auch im Rechner-Layout (Tablets): der Lage-Hover kennt keinen Finger.
  const fingerDrag = isMobile || dnd.activeIsTouch;
  const dockShownRaised = dockRaised || (fingerDrag && !!dnd.activeCard);
  const handLowered = fingerDrag && !!dnd.activeCard;
  // Zähler aus `deck`, nicht aus `deckCount`: nur das Array führen die Reducer nach.
  const deck = me.deck || [];
  const graveyard = me.graveyard || [];
  const exile = me.exile || [];
  const commandZone = me.commandZone || [];
  const showCommandZone = state.isCommanderMode || commandZone.length > 0;

  const deckTop = me.deckOpenPlay ? topOf(deck) : null;
  // Oberkarten sind Zieh-Quellen, solange kein Viewer offen ist: dessen Karten tragen dieselbe
  // dnd-kit-ID (siehe ZoneTile).
  const tileDraggable = canAct && !viewer;
  // Am Handy gehört der Langdruck auf der Bibliothek dem Bibliotheks-Sheet (`deckLongPress`); ein
  // unbewegter Zug der offenen Oberkarte öffnete zugleich das Karten-Sheet.
  const deckTopDraggable = tileDraggable && !isMobile;
  // Je Kachel ein fester Schlüssel: wechselt die Oberkarte unter dem Zeiger, folgt die Vorschau.
  const tilePreview = (zone, card) => hoverBind(card || null, { key: `tile:${zone}` });
  const deckTile = (
    <ZoneTile
      zone="deck"
      count={deck.length}
      topCard={deckTop}
      isOwn
      draggableTop={deckTopDraggable}
      dropId={{ top: DROP_IDS.deckTop, bottom: DROP_IDS.deckBottom }}
      onClick={canAct ? () => socket.drawCard(1) : undefined}
      previewHandlers={tilePreview('deck', deckTop)}
      onTopCardFlip={previewFlipFor(deckTop)}
      contextMenu={!canAct ? undefined : isMobile
        ? (tile) => <span className="playtest-board__press" {...deckLongPress}>{tile}</span>
        : (tile) => <ActionContextMenu actions={deckActions(ctx)}>{tile}</ActionContextMenu>}
    />
  );

  const dock = (
    <BoardDock
      ref={dockRef}
      raised={dockShownRaised}
      onRaisedChange={setDockRaised}
      isMobile={isMobile}
      handLowered={handLowered}
      tiles={{
        left: (
          <>
            <ZoneTile
              zone="graveyard"
              cards={graveyard}
              topCard={topOf(graveyard)}
              isOwn
              draggableTop={tileDraggable}
              dropId={DROP_IDS.graveyard}
              onOpenViewer={() => openViewer('graveyard')}
              previewHandlers={tilePreview('graveyard', topOf(graveyard))}
              onTopCardFlip={previewFlipFor(topOf(graveyard))}
            />
            <ZoneTile
              zone="exile"
              cards={exile}
              topCard={topOf(exile)}
              isOwn
              draggableTop={tileDraggable}
              dropId={DROP_IDS.exile}
              hidden={exile.length === 0 && !dnd.activeCard}
              onOpenViewer={() => openViewer('exile')}
              previewHandlers={tilePreview('exile', topOf(exile))}
              onTopCardFlip={previewFlipFor(topOf(exile))}
            />
          </>
        ),
        right: (
          <>
            {showCommandZone && (
              <ZoneTile
                zone="commandZone"
                cards={commandZone}
                topCard={commandZone[0] || null}
                isOwn
                draggableTop={tileDraggable}
                dropId={DROP_IDS.commandZone}
                onOpenViewer={() => openViewer('commandZone')}
                previewHandlers={tilePreview('commandZone', commandZone[0])}
                onTopCardFlip={previewFlipFor(commandZone[0])}
                symbol={state.isCommanderMode && (
                  <CommanderTaxMenu value={taxValue} disabled={!canAct} onChange={changeCommanderTax} />
                )}
              />
            )}
            {deckTile}
          </>
        ),
      }}
      hand={(
        <HandFan
          cards={(me.hand || []).filter(Boolean)}
          raised={dockShownRaised}
          onRaisedChange={setDockRaised}
          closeOnBack={isMobile && dockRaised}
          isMobile={isMobile}
          canDrag={canAct}
          onCardOpenMenu={(card, point) => openCardActions({ instanceId: card.instanceId, zone: 'hand', ownerId: me.userId }, point)}
          previewBind={hoverBind}
          previewFlipFor={previewFlipFor}
        />
      )}
    />
  );

  // Vor dem Dock im DOM: das gehobene Dock liegt über dem Knopf.
  const protocolButton = (
    <Tooltip label="Protokoll">
      <button
        type="button"
        className="playtest-board__protocol"
        data-testid="protocol-button"
        onClick={() => setProtocolOpen(true)}
      >
        <MessageSquare size={18} aria-hidden />
      </button>
    </Tooltip>
  );

  const openParticipantZone = (userId, zone) => {
    if (zone !== 'deck') {
      openViewer(zone, userId);
      return;
    }
    // Die Bibliothek eines anderen zeigt nur bei offenem Spiel ihre Oberkarte.
    const owner = state.players.find((p) => sameUser(p.userId, userId));
    if (owner?.deckOpenPlay && topOf(owner.deck)) setViewer({ zone: 'deck', userId });
  };

  // Rechts in der Köpfe-Zeile: Hinweise und am Handy die Zug-Zeile.
  const topTrailing = (!canAct || socket.reconnecting || isMobile) ? (
    <>
      {!canAct && (
        <Badge tone="pending" data-testid="spectator-notice">
          <Eye size={12} aria-hidden />
          Du siehst zu
        </Badge>
      )}
      {socket.reconnecting && (
        <Tooltip label="Verbindung unterbrochen, verbinde neu …">
          <span tabIndex={0}>
            <Badge tone="danger" data-testid="offline-marker">
              <WifiOff size={12} aria-hidden />
              Offline
            </Badge>
          </span>
        </Tooltip>
      )}
      {isMobile && (
        <div className="playtest-board__turn" data-testid="turn-line">
          <span className="playtest-board__turn-number">Zug {state.turnNumber}</span>
          {/* Ein Zuschauer kommt nie an den Zug; der Platz gehört dann den Köpfen. */}
          {canAct && (
            <button type="button" className="btn-primary playtest-board__end-turn" disabled={!isMyTurn} onClick={socket.endTurn}>
              Zug beenden
            </button>
          )}
        </div>
      )}
    </>
  ) : null;

  const battlefield = (
    <Battlefield
      players={displayPlayers}
      pings={state.pings}
      viewerUserId={state.currentUserId}
      currentTurnUserId={state.currentTurnUserId}
      playerCount={playerCount}
      camera={camera}
      onBackgroundContextMenu={
        // Zuschauer haben kein Flächen-Menü; das Browser-Menü soll trotzdem nicht aufgehen.
        !canAct ? (_world, event) => event.preventDefault()
          : isMobile ? (world) => setBoardSheet({ position: world }) : undefined
      }
      onCardTap={onCardTap}
      onCardPing={deferredTap.onDoubleClick}
      onCardBringToFront={socket.bringToFront}
      onCardOpenMenu={(instanceId, point) => openCardActions(battlefieldTarget(instanceId), point)}
      previewFlipFor={previewFlipFor}
      onCardCounterAdd={socket.addCounter}
      onCardCounterRemove={socket.removeCounter}
      onCardHoverPreview={cardHover}
      canAct={canAct}
      isMobile={isMobile}
    />
  );

  const cardMenu = (
    <CardContextMenu point={menuData?.card ? menu.point : null} actions={menuData?.actions || []} onClose={() => setMenu(null)} />
  );
  const cardSheet = (
    <CardActionSheet
      open={!!sheetData?.card}
      onOpenChange={(open) => { if (!open) setSheetTarget(null); }}
      card={sheetData?.card || null}
      actions={sheetData?.actions || []}
    />
  );

  return (
    <MotionConfig reducedMotion="user">
      <DndContext
        sensors={dnd.sensors}
        collisionDetection={dnd.collisionDetection}
        onDragStart={onDragStart}
        onDragMove={dnd.onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
        autoScroll={false}
        measuring={dnd.measuring}
        accessibility={DND_ACCESSIBILITY}
      >
        <div
          className={`playtest-board${isMobile ? ' playtest-board--mobile' : ''}`}
          data-testid="playtest-board"
          data-can-act={canAct ? 'true' : 'false'}
          style={{
            '--ptb-dock-rest': `${dockRestPx}px`,
            '--ptb-gap': `${DOCK_SPACING.gapPx}px`,
            '--ptb-protocol': `${DOCK_SPACING.protocolPx}px`,
          }}
          onPointerDownCapture={() => {
            if (!isMobile || landscapeRequestedRef.current) return;
            landscapeRequestedRef.current = true;
            requestLandscape();
          }}
        >
          <BoardArea
            isMobile={isMobile}
            pressHandlers={isMobile ? boardPressHandlers : null}
            dockReservePx={railDockReservePx}
            top={(
              <ParticipantRow
                ref={headsRef}
                floating
                onExpandedChange={setHeadsExpanded}
                players={state.players}
                viewerUserId={state.currentUserId}
                currentTurnUserId={state.currentTurnUserId}
                compactOpponents={isMobile || playerCount >= 4}
                isMobile={isMobile}
                isCommanderMode={state.isCommanderMode}
                connected={socket.connected}
                canAct={canAct}
                onLifeCommit={socket.updateLife}
                onCounterChange={socket.updatePlayerCounter}
                onCounterAdd={socket.updatePlayerCounter}
                onCounterRemove={socket.removePlayerCounter}
                onCommanderDamageChange={socket.updateCommanderDamage}
                onZoneClick={openParticipantZone}
                onRequestHand={canAct ? socket.requestViewHand : undefined}
                previewBind={hoverBind}
                trailing={topTrailing}
              />
            )}
            topReservePx={headsReservePx}
            bottom={<>{protocolButton}{dock}</>}
            rail={(maxHeight) => (
              <ActionRail
                onUntapAndDraw={canAct ? untapAndDraw : undefined}
                onEndTurn={isMobile ? undefined : socket.endTurn}
                canEndTurn={isMyTurn}
                onHome={camera.goHome}
                onRollDice={canAct ? socket.rollDice : undefined}
                more={more}
                maxHeight={maxHeight}
                isMobile={isMobile}
              />
            )}
          >
            {isMobile || !canAct ? battlefield : (
              <BattlefieldContextMenu
                onCreateToken={(point) => setTokenPosition(camera.toWorld(point) || null)}
                onRollDice={socket.rollDice}
                onUntapAll={socket.untapAll}
              >
                {battlefield}
              </BattlefieldContextMenu>
            )}
          </BoardArea>

          {state.isInMulliganPhase && canAct && (
            <MulliganOverlay
              hand={(me.hand || []).filter(Boolean)}
              mulliganCount={me.mulliganCount || 0}
              recommendedBottomCount={state.cardsToBottomCount}
              isCommanderMode={state.isCommanderMode}
              kept={!!me.mulliganKept}
              // Auch Abwesende stehen hier: wann ein Abwesender als behalten zählt (nach der
              // Schonfrist), entscheidet der Server — ein Neuladender soll weiter genannt sein.
              waitingFor={state.players
                .filter((p) => !p.mulliganKept && !sameUser(p.userId, me.userId) && p.playerStatus !== 'eliminated')
                .map((p) => p.displayName)}
              onTakeMulligan={socket.takeMulligan}
              onKeep={socket.keepHand}
              previewBind={hoverBind}
              previewFlipFor={previewFlipFor}
              isMobile={isMobile}
            />
          )}
        </div>

        {createPortal(
          <DragOverlay dropAnimation={null} zIndex={1100}>
            {dnd.activeCard && <DragOverlayCard card={dnd.activeCard} sizePx={cardSizePx} offset={dnd.overlayOffset} />}
          </DragOverlay>,
          document.body,
        )}

        <CardListViewer
          open={!!viewer}
          onOpenChange={(open) => { if (!open) setViewer(null); }}
          title={viewerTitle}
          zone={viewer?.zone}
          cards={viewerCards}
          allowDrag={viewerIsOwn && canAct}
          onCardAction={viewerIsOwn && canAct
            ? (card, point) => openCardActions({ instanceId: card.instanceId, zone: viewer.zone, ownerId: viewer.userId }, point)
            : undefined}
        >
          {viewerMenuOpen && cardMenu}
          {viewerSheetOpen && cardSheet}
        </CardListViewer>
        {!viewerMenuOpen && cardMenu}
        {!viewerSheetOpen && cardSheet}
      </DndContext>

      <FloatingCardPreview preview={preview} />
      <ToastPlacement side="right" bottom={toastBottomPx} />
      <SchemaModal
        open={socket.reconnectFailed && !state.boardClosed}
        onOpenChange={(open) => { if (!open) leaveToEntry(); }}
        size="sm"
        title="Verbindung verloren"
        footer={(
          <>
            <button type="button" className="btn-secondary" onClick={leaveToEntry}>Zum Einstieg</button>
            <button type="button" className="btn-primary" onClick={socket.reconnect}>Neu verbinden</button>
          </>
        )}
      >
        <p className="playtest-board__dialog-text">
          Der Server antwortet nicht. Versuche es erneut oder kehre zum Einstieg zurück.
        </p>
      </SchemaModal>

      <ScryMillDialog
        open={!!scryMill}
        mode={scryMill || 'scry'}
        max={deck.filter(Boolean).length}
        onConfirm={confirmScryMill}
        onOpenChange={(open) => { if (!open) setScryMill(null); }}
      />
      <TokenPicker
        open={tokenPosition !== undefined}
        onOpenChange={(open) => { if (!open) setTokenPosition(undefined); }}
        available={me.tokensAvailable || []}
        onPick={pickToken}
      />
      <ProtocolSheet open={protocolOpen} onOpenChange={setProtocolOpen} entries={state.sessionLog} />
      <SchemaModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} size="sm" title="Tastenkürzel">
        <p className="playtest-board__shortcuts-hint">Die Tasten gelten für die Karte unter dem Zeiger, sonst für die zuletzt getappte; R gilt für die stehende Großansicht.</p>
        <dl className="playtest-board__shortcuts">
          {SHORTCUTS.map(([key, label]) => (
            <React.Fragment key={key}>
              <dt><kbd>{key}</kbd></dt>
              <dd>{label}</dd>
            </React.Fragment>
          ))}
        </dl>
      </SchemaModal>

      {isMobile && (
        <>
          <ActionSheet
            open={!!boardSheet}
            onOpenChange={(open) => { if (!open) setBoardSheet(null); }}
            title="Brett"
            items={boardActions({
              onCreateToken: (position) => setTokenPosition(position ?? null),
              onRollDice: socket.rollDice,
              onUntapAll: socket.untapAll,
            }, boardSheet?.position)}
          />
          <ActionSheet open={deckSheetOpen} onOpenChange={setDeckSheetOpen} title="Bibliothek" items={deckActions(ctx)} />
        </>
      )}

      {dialogs}
    </MotionConfig>
  );
}

/**
 * Brettfläche vom oberen bis zum unteren Rand; darüber schweben die Köpfe (`top`), die
 * Aktionsleiste und unten Protokoll-Knopf und Dock (`bottom`). Die Leiste beginnt
 * `topReservePx` unter dem oberen Rand und endet `dockReservePx` über dem unteren; die Höhe
 * dazwischen bekommt sie als `maxHeight` (Prüfanker `data-max-height` an ihrem Behälter).
 */
function BoardArea({ isMobile, pressHandlers, top, rail, bottom, topReservePx = 0, dockReservePx = 0, children }) {
  const [measureRef, height] = useElementHeight();
  const railSpace = height == null ? undefined : Math.max(0, height - 16 - topReservePx - dockReservePx);
  return (
    <div className="playtest-board__area" ref={measureRef} {...(pressHandlers || {})}>
      {children}
      {top}
      <div
        className={`playtest-board__rail playtest-board__rail--${isMobile ? 'right' : 'left'}`}
        style={{ '--ptb-rail-top': `${topReservePx}px` }}
        data-max-height={railSpace}
      >
        {rail(railSpace)}
      </div>
      {bottom}
    </div>
  );
}
